import React from "react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import FormSelect from "@ats/src/components/forms/FormSelect";
import FormContainer from "@ats/src/components/forms/FormContainer";

import styled from "@emotion/styled";
import { css } from "@emotion/react";

import { useToastContext } from "@shared/context/ToastContext";

import {
  adminJobStatusOptions,
  adminInternalAdminStatusOptions,
} from "@ats/src/lib/lookups";

import { useUpdateAdminJob } from "@shared/queryHooks/admin/useAdminJobs";

type Props = {
  job: any;
  onCancel: () => void;
};

function AdminJobEditModal(props: Props) {
  const { job: passedJob, onCancel } = props;
  const [internalAdminStatus, setInternalAdminStatus] = React.useState(passedJob.internalAdminStatus || "pending");
  const [visibilityState, setVisibilityState] = React.useState({
    status: passedJob.status,
    visible: passedJob.visible,
  });
  const [errors, setErrors] = React.useState(null);
  const [isDirty, setIsDirty] = React.useState(false);
  const addToast = useToastContext();

  const { mutate: updateAdminJob, isLoading } = useUpdateAdminJob();

  const { id, title, organizationName } = passedJob;

  // Determine if job is published
  const isPublished = passedJob.status === "published";

  window.logger("%c[AdminJobEditModal] RENDER", "background-color: #8fb4d8", {
    passedJob,
    visibilityState,
    internalAdminStatus,
    isPublished,
  });

  const handleStatusChange = (name, value) => {
    window.logger("%c[AdminJobEditModal] handleStatusChange", "color: #1976D2", { name, value });
    if (value === "status_archived") {
      setVisibilityState({ status: "status_archived", visible: true });
    } else if (value === "draft") {
      setVisibilityState({ status: "draft", visible: true });
    }
    setErrors(null);
    setIsDirty(true);
  };

  const handleInternalAdminStatusChange = (name, value) => {
    window.logger("%c[AdminJobEditModal] handleInternalAdminStatusChange", "color: #1976D2", { name, value });
    setInternalAdminStatus(value);
    setErrors(null);
    setIsDirty(true);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    updateAdminJob(
      {
        id,
        internalAdminStatus,
        ...visibilityState,
      },
      {
        onSuccess: (data) => {
          window.logger("%c[AdminJobEditModal] handleSubmit success", "color: #1976D2", { data });
          addToast({
            kind: "success",
            title: "Job updated successfully",
          });
          onCancel();
        },
        onError: (response: any) => {
          if (response.data?.errors) {
            setErrors(response.data.errors);
          }
          addToast({
            kind: "warning",
            title: "Could not update job",
          });
        },
      },
    );
  };

  // Build status options based on current status
  const statusOptions = isPublished
    ? [{ value: "published", label: "Published" }, ...adminJobStatusOptions]
    : adminJobStatusOptions;

  const submitButton = (
    <Styled.ButtonContainer>
      <Button type="button" onClick={handleSubmit} loading={isLoading}>
        Update job
      </Button>
      <Button styleType="secondary" onClick={onCancel}>
        Cancel
      </Button>
    </Styled.ButtonContainer>
  );

  return (
    <CenterModal
      headerTitleText={`Edit Job: ${title}`}
      onCancel={onCancel}
      hasUnsavedChanges={isDirty}
    >
      <FormContainer errors={errors} buttons={submitButton} className="admin-modal">
        <Styled.JobInfo>
          <div>
            <strong>Job ID:</strong> {id}
          </div>
          <div>
            <strong>Organization:</strong> {organizationName}
          </div>
          <div>
            <strong>Current Status:</strong> {passedJob.status}
          </div>
        </Styled.JobInfo>

        <FormSelect
          onChange={handleStatusChange}
          name="status"
          label="Status"
          description={
            isPublished
              ? "Changing to Draft will revert this published job. Archived will archive it."
              : "Change the job status"
          }
          value={visibilityState.status}
          options={statusOptions}
          errors={errors}
        />

        <FormSelect
          onChange={handleInternalAdminStatusChange}
          name="internalAdminStatus"
          label="Internal Admin Status"
          description="Admin-only status for tracking and workflow purposes"
          value={internalAdminStatus}
          options={adminInternalAdminStatusOptions}
          errors={errors}
        />
      </FormContainer>
    </CenterModal>
  );
}

export default AdminJobEditModal;

/* Styled Components
======================================================= */
const Styled: any = {};

Styled.JobInfo = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminJobEditModal_JobInfo;
    ${[t.mb(6), t.p(4)]}
    background-color: ${t.color.gray[100]};
    border-radius: 4px;

    div {
      ${t.mb(2)}

      &:last-of-type {
        ${t.mb(0)}
      }
    }
  `;
});

Styled.ButtonContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminJobEditModal_ButtonContainer;
    display: flex;
    > * {
      ${t.mr(3)}

      &:last-of-type {
        ${t.mr(0)}
      }
    }
  `;
});
